import React, { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { AlertTriangle, CheckCircle, Search, XCircle } from "react-feather";
import { useGetLazy } from "../../api";
import {
  IProductsResponse,
  IOpenDataMerchantMapCapabilities,
  IOpenDataMerchantMapResult,
  IOpenDataMerchantMapSection,
  OpenDataCheckStatus,
  OpenDataViewType,
} from "../../interfaces";
import { JsonBlock } from "./json-block";

const STATUS_VARIANT: Record<OpenDataCheckStatus, string> = {
  [OpenDataCheckStatus.Pass]: "success",
  [OpenDataCheckStatus.Warn]: "warning",
  [OpenDataCheckStatus.Fail]: "danger",
  [OpenDataCheckStatus.Error]: "secondary",
};

const CAPABILITY_LABELS: [keyof IOpenDataMerchantMapCapabilities, string][] = [
  ["product", "Product"],
  ["autoTemplate", "Auto template"],
  ["skuTemplate", "SKU template"],
  ["shipping", "Shipping"],
];

const sectionVariant = (s: IOpenDataMerchantMapSection): string =>
  s.filled === 0 ? "danger" : s.filled === s.total ? "success" : "warning";

const SectionCard: React.FC<{
  section: IOpenDataMerchantMapSection;
  map: Record<string, any>;
}> = ({ section, map }) => {
  const [showEmpty, setShowEmpty] = useState(false);
  const values = (map[section.key] ?? {}) as Record<string, unknown>;
  const fields = showEmpty
    ? [...section.populatedFields, ...section.emptyFields]
    : section.populatedFields;

  return (
    <Col lg={6}>
      <Card className={`h-100 border-${sectionVariant(section)}`}>
        <Card.Header className="d-flex justify-content-between align-items-center small fw-bold">
          <span className="d-flex align-items-center gap-1">
            {section.filled > 0 ? (
              <CheckCircle size={16} className={`text-${sectionVariant(section)}`} />
            ) : (
              <XCircle size={16} className="text-danger" />
            )}
            {section.label}
          </span>
          <Badge bg={sectionVariant(section)}>
            {section.filled}/{section.total} set
          </Badge>
        </Card.Header>
        <Card.Body>
          <ProgressBar
            now={section.total ? (section.filled / section.total) * 100 : 0}
            variant={sectionVariant(section)}
            className="mb-3"
            style={{ height: 6 }}
          />
          {section.filled === 0 ? (
            <div className="text-muted small">
              Nothing configured in this section.
            </div>
          ) : (
            <Table size="sm" className="small mb-0">
              <tbody>
                {fields.map((f) => {
                  const v = values[f];
                  const empty = !section.populatedFields.includes(f);
                  return (
                    <tr key={f} className={empty ? "text-muted" : undefined}>
                      <td style={{ width: "45%" }}>{f}</td>
                      <td className="text-break">
                        {empty ? (
                          <em>not set</em>
                        ) : (
                          <code>{String(v)}</code>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
          {section.emptyFields.length > 0 && section.filled > 0 && (
            <Button
              size="sm"
              variant="link"
              className="p-0 mt-2"
              onClick={() => setShowEmpty((s) => !s)}
            >
              {showEmpty
                ? "Hide unset fields"
                : `Show ${section.emptyFields.length} unset field${section.emptyFields.length === 1 ? "" : "s"}`}
            </Button>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

export const MerchantMap: React.FC = () => {
  const [domain, setDomain] = useState("");
  const [country, setCountry] = useState("");
  const [viewType, setViewType] = useState<OpenDataViewType>(
    OpenDataViewType.Mobile
  );

  const { data, loading, error, execute } =
    useGetLazy<IOpenDataMerchantMapResult>();
  // What the map can drive is only half the story — the other half is whether
  // we hold any products to drive it with.
  const { data: geoProducts, execute: fetchGeoProducts } =
    useGetLazy<IProductsResponse>();
  const { data: allProducts, execute: fetchAllProducts } =
    useGetLazy<IProductsResponse>();
  const [productGeo, setProductGeo] = useState("");

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const clean = domain
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .replace(/\/.*$/, "");
    if (!clean) return;
    const geo = country.trim().toUpperCase();
    setProductGeo(geo);
    execute(
      `open-data/merchant-map?domain=${encodeURIComponent(clean)}&viewType=${viewType}`
    ).catch(() => undefined);
    fetchAllProducts(
      `affiliate-products/products?domain=${encodeURIComponent(clean)}&limit=1`
    ).catch(() => undefined);
    if (geo) {
      fetchGeoProducts(
        `affiliate-products/products?domain=${encodeURIComponent(clean)}&countryCode=${encodeURIComponent(geo)}&limit=1`
      ).catch(() => undefined);
    }
  };

  const totalFilled = data?.sections.reduce((s, x) => s + x.filled, 0) ?? 0;
  const totalFields = data?.sections.reduce((s, x) => s + x.total, 0) ?? 0;

  return (
    <div>
      <div className="mb-3 d-flex align-items-center gap-2 small">
        <Badge bg="primary">Open Data API</Badge>
        <code className="text-muted">GET /v1/OpenData/merchant-map</code>
      </div>
      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={onSubmit}>
            <Row className="align-items-end g-3">
              <Col md={5}>
                <Form.Group>
                  <Form.Label className="small fw-bold">Domain</Form.Label>
                  <Form.Control
                    placeholder="e.g. currys.co.uk"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small fw-bold">
                    Country{" "}
                    <span className="text-muted fw-normal">(optional)</span>
                  </Form.Label>
                  <Form.Control
                    placeholder="US"
                    value={country}
                    onChange={(e) => setCountry(e.target.value.toUpperCase())}
                    style={{ textTransform: "uppercase" }}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small fw-bold">View type</Form.Label>
                  <Form.Select
                    value={viewType}
                    onChange={(e) =>
                      setViewType(e.target.value as OpenDataViewType)
                    }
                  >
                    <option value={OpenDataViewType.Mobile}>Mobile</option>
                    <option value={OpenDataViewType.Desktop}>Desktop</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Button
                  type="submit"
                  variant="primary"
                  className="w-100"
                  disabled={loading || !domain.trim()}
                >
                  {loading ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    <>
                      <Search size={16} className="me-1" /> Look up
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger">
          Merchant map lookup failed. Please try again.
        </Alert>
      )}

      {allProducts && (
        <Card className="mb-4">
          <Card.Header className="fw-bold small d-flex justify-content-between align-items-center">
            <span>
              Products ingested
              <span className="text-muted fw-normal ms-2">
                what the map would have to work with
              </span>
            </span>
            <Badge bg="secondary">Internal API</Badge>
          </Card.Header>
          <Card.Body className="py-3">
            <div className="d-flex flex-wrap gap-4 align-items-end">
              {productGeo && (
                <div>
                  <div className="fs-4 fw-semibold">
                    {(geoProducts?.totalCount ?? 0).toLocaleString()}
                  </div>
                  <div className="small fw-semibold">in {productGeo}</div>
                </div>
              )}
              <div>
                <div className="fs-4 fw-semibold">
                  {(allProducts.totalCount ?? 0).toLocaleString()}
                </div>
                <div className="small fw-semibold">across every geo</div>
              </div>
            </div>
            <div className="small text-muted mt-2">
              {productGeo && (geoProducts?.totalCount ?? 0) === 0
                ? `Nothing ingested for ${productGeo}. A complete map still has no products to apply it to in that geo.`
                : productGeo
                  ? `Counted from the products' own regions, not the advertiser's — an advertiser can carry ${productGeo} products with no region set.`
                  : "Add a country above to see how many of these are ingested for one geo."}
            </div>
          </Card.Body>
        </Card>
      )}

      {data && !data.found && (
        <Alert variant="warning">
          <XCircle size={16} className="me-1" />
          No <strong>{data.viewType}</strong> merchant map published for{" "}
          <strong>{data.domain}</strong>
          {data.message ? ` — ${data.message}.` : "."}
        </Alert>
      )}

      {data?.found && data.map && (
        <>
          <Alert
            variant={STATUS_VARIANT[data.status]}
            className="d-flex flex-wrap gap-3 align-items-center"
          >
            <span>
              {data.status === OpenDataCheckStatus.Pass ? (
                <CheckCircle size={16} className="me-1" />
              ) : (
                <AlertTriangle size={16} className="me-1" />
              )}
              <strong>{data.map.domain}</strong>
            </span>
            <span>{data.statusReason}</span>
            {CAPABILITY_LABELS.map(([key, label]) => (
              <Badge
                key={key}
                bg={data.capabilities[key] ? "light" : "secondary"}
                text={data.capabilities[key] ? "dark" : undefined}
                className={data.capabilities[key] ? "border" : "opacity-50"}
              >
                {data.capabilities[key] ? "✓" : "✕"} {label}
              </Badge>
            ))}
            <span>
              View: <Badge bg="dark">{data.map.viewType}</Badge>
            </span>
            <span>
              Version: <Badge bg="secondary">{data.map.version}</Badge>
            </span>
            <span>
              Fields set:{" "}
              <Badge bg="primary">
                {totalFilled}/{totalFields}
              </Badge>
            </span>
            <span className="text-muted small">id: {data.map.id}</span>
          </Alert>

          <Row className="g-3">
            {data.sections.map((s) => (
              <SectionCard
                key={s.key}
                section={s}
                map={data.map as unknown as Record<string, any>}
              />
            ))}
          </Row>
        </>
      )}

      {data && <JsonBlock value={data.map ?? data} title="Raw merchant-map payload" />}
    </div>
  );
};
